// The sidebar rail's model — pure. No DOM, no queries, no router.
//
// The rail is organised by JOB, not by feature (the design canvas, 08 / APP):
// ORIENT · WATCH · DECIDE · PROVE & CONFIGURE · SETTINGS, five sections that all
// fold the same way (`components/RailGroups.tsx`). Which group a screen belongs
// to is stated ONCE, on its route entry (`RouteEntry.group`, required); this
// module states only what is the rail's own — the ORDER of the rows inside a group.
//
// Both halves are checked at import, so a drift throws in every test that loads
// the shell rather than shipping a screen nobody can reach:
//   - an order list naming a route that is not in that group, or has no label;
//   - a route whose group no rail renders (`assertRailCovers`).
//
// The TABLES are whole; what a caller may SEE is not. The scope gate at the
// bottom (`filterRailItemsByScope` / `filterRailGroupsByScope`) drops the rows
// whose scope the caller's `/v1/me` does not list — it filters the RENDERED
// rows, never the tables above, so the import-time checks still see every
// grouped route.

import { Scope } from '@developerz/domain';
import type { NavGroup, OrgRouteKey, RouteEntry } from './routes';
import { ORG_ROUTE_TABLE, TOP_ROUTE_TABLE } from './routes';

export type RailItem = {
  /** The route key — what a count is attached by. */
  readonly key: string;
  /** Unresolved route path; the shell's `resolve` org-prefixes it. */
  readonly href: string;
  /** i18n key of the route's label. */
  readonly label: string;
  /** Exact-match active state: `currentRailItem` lights this row only on its own path. */
  readonly end?: boolean;
};

/** A rail group is never empty, so `items[0]` is always a row. */
export type Rail = readonly [RailItem, ...RailItem[]];

export type RailGroup = {
  readonly group: NavGroup;
  /** i18n key of the mono group heading. */
  readonly heading: string;
  readonly items: Rail;
};

function rail<K extends string>(
  table: readonly RouteEntry<K>[],
  group: NavGroup,
  order: readonly NoInfer<K>[],
  exact: readonly NoInfer<K>[] = [],
): Rail {
  const items = order.map((key): RailItem => {
    const entry = table.find((r) => r.key === key);
    if (entry === undefined) throw new Error(`rail: no route '${key}' (lib/routes.ts)`);
    if (entry.group !== group) {
      throw new Error(
        `rail: route '${key}' is in group ${String(entry.group)}, listed under '${group}' — fix its \`group\` in lib/routes.ts or move it in lib/rail.ts`,
      );
    }
    // A link needs a named, single address; a `label: null` route has neither.
    if (entry.label === null) throw new Error(`rail: route '${key}' has no label (lib/routes.ts)`);
    return {
      key,
      href: entry.path,
      label: entry.label,
      ...(exact.includes(key) && { end: true }),
    };
  });
  const [first, ...rest] = items;
  if (first === undefined) throw new Error(`rail: group '${group}' needs at least one route`);
  return [first, ...rest];
}

/** Every grouped route in `table` is a row of some rail; throws naming the orphans. */
function assertRailCovers(table: readonly RouteEntry[], rails: readonly Rail[]): void {
  const listed = new Set(rails.flatMap((r) => r.map((it) => it.key)));
  const orphans = table.filter((r) => r.group !== null && !listed.has(r.key)).map((r) => r.key);
  if (orphans.length > 0) {
    throw new Error(
      `rail: route(s) ${orphans.join(', ')} carry a group but no rail lists them — add each to its group's order in lib/rail.ts`,
    );
  }
}

/** The five job groups, in rail order. Every one folds (`components/RailGroups.tsx`). */
export const ORG_RAIL: readonly RailGroup[] = [
  {
    group: 'orient',
    heading: 'nav.group_orient',
    items: rail(ORG_ROUTE_TABLE, 'orient', ['home', 'repos', 'dashboards', 'digests']),
  },
  {
    group: 'watch',
    heading: 'nav.group_watch',
    items: rail(ORG_ROUTE_TABLE, 'watch', [
      'agents',
      'terminal',
      'fleet',
      'plans',
      'ci',
      'artifacts',
    ]),
  },
  {
    group: 'decide',
    heading: 'nav.group_decide',
    items: rail(ORG_ROUTE_TABLE, 'decide', ['pending', 'blockers']),
  },
  {
    group: 'prove',
    heading: 'nav.group_prove',
    items: rail(ORG_ROUTE_TABLE, 'prove', [
      'audit',
      'account_keys',
      'notifications_settings',
      'billing',
    ]),
  },
  {
    // Configuration set once, unrolled deliberately.
    group: 'settings',
    heading: 'nav.group_settings',
    items: rail(ORG_ROUTE_TABLE, 'settings', [
      'account_members',
      'account_squads',
      'account_sso',
      'my_keys',
      'github_token',
      'logins',
      'account_tokens',
      'system_prompts',
      'account_templates',
      'account_prompt_vars',
      'account_webhooks',
      'email_routes',
      'integrations',
      'donations',
    ]),
  },
];

// admin.developerz.ai — rendered ONLY for `developerz-ai` staff. `/admin` is
// exact-match: every other radar screen nests under it. The order is the access
// funnel as an operator walks it: who asked, who is in, what was mailed, what
// they hold.
export const ADMIN_NAV: Rail = rail(
  TOP_ROUTE_TABLE,
  'admin',
  [
    'admin_overview',
    'admin_access_requests',
    'admin_users',
    'admin_invitations',
    'admin_grants',
    'admin_orgs',
    'admin_oss',
    'admin_goals',
  ],
  ['admin_overview'],
);

/** The admin host's rail: one group, folded by the same mechanism as the org rail. */
export const ADMIN_RAIL: readonly RailGroup[] = [
  { group: 'admin', heading: 'nav.admin', items: ADMIN_NAV },
];

/** Every org destination on the rail, flat — what the topbar crumb resolves against. */
export const NAV: readonly RailItem[] = ORG_RAIL.flatMap((g) => g.items);

/**
 * The user block's menu (`components/UserMenu.tsx`): the `/account/*` screens
 * about the signed-in PERSON, never the org's settings: their personal keys and
 * GitHub user token (`/v1/me/*`), their connected logins (`/account/logins`,
 * `/v1/me/identities`, #466), and their notification preferences
 * (`/v1/me/notification-preferences`, plus their own push devices,
 * `/v1/push/subscriptions`).
 * Every one is also a rail row, so it is read off `NAV`: a route dropped from
 * the rail throws here at import instead of leaving a dead menu row.
 */
export const USER_MENU: readonly RailItem[] = (
  [
    'my_keys',
    'github_token',
    'logins',
    'notifications_settings',
  ] as const satisfies readonly OrgRouteKey[]
).map((key) => {
  const item = NAV.find((it) => it.key === key);
  if (item === undefined) throw new Error(`rail: the user menu names '${key}', no rail row`);
  return item;
});

assertRailCovers(
  ORG_ROUTE_TABLE,
  ORG_RAIL.map((g) => g.items),
);
assertRailCovers(
  TOP_ROUTE_TABLE,
  ADMIN_RAIL.map((g) => g.items),
);

/**
 * The ONE scope gate for the navigation.
 *
 * `write:self:keys` is the caller's OWN inference authority — their personal
 * BYOK keys (`/v1/me/keys`) and their GitHub user token (`/v1/me/github-token`).
 * It rides the MEMBER ceiling (`packages/domain/src/scopes.ts`), so a viewer
 * does not hold it and both screens answer 403 for one.
 *
 * The gate filters the RENDERED rows, never the tables: `ORG_RAIL`, `NAV` and
 * `USER_MENU` stay WHOLE, so `assertRailCovers` still sees every grouped route
 * and the user menu still names a row the rail has. A row with no gate here is
 * never dropped — every other screen on the rail is gated by its own route's
 * `requireScope`, and the server is the authority on those.
 *
 * The map is keyed by `OrgRouteKey`, so a MISSPELLED row key is a compile
 * error rather than a row that silently carries no gate.
 */
const SCOPED_ROWS: Readonly<Partial<Record<OrgRouteKey, string>>> = {
  my_keys: Scope.WriteSelfKeys,
  github_token: Scope.WriteSelfKeys,
};

/**
 * A rail group as the scope gate renders it: the same group, with the rows the
 * caller's scopes do not cover dropped. `items` is a plain list here, not the
 * non-empty `Rail` tuple — a filter may empty a group, and the type says so.
 */
export type ScopedRailGroup = {
  readonly group: NavGroup;
  readonly heading: string;
  readonly items: readonly RailItem[];
};

/**
 * The rows the caller's scope list does not cover, dropped.
 *
 * An ABSENT scope list keeps every row: the server is the authority (it
 * refuses what it must), and a `/v1/me` that publishes no list — a legacy
 * answer, a partial mock — must not silently empty the navigation. A list that
 * IS published is read as the truth about this credential, never re-derived
 * from its role.
 */
export function filterRailItemsByScope(
  items: readonly RailItem[],
  scopes: readonly string[] | undefined,
): readonly RailItem[] {
  if (scopes === undefined) return items;
  return items.filter((item) => {
    // `RailItem.key` is a plain `string`, so the lookup is narrowed back to the
    // union the map is keyed by — a key that is not an org route has no gate.
    const need = SCOPED_ROWS[item.key as OrgRouteKey];
    return need === undefined || scopes.includes(need);
  });
}

/** The rail groups the caller's scope list narrows — the same groups, fewer rows. */
export function filterRailGroupsByScope(
  groups: readonly RailGroup[],
  scopes: readonly string[] | undefined,
): readonly ScopedRailGroup[] {
  return groups.map((group) => ({
    ...group,
    items: filterRailItemsByScope(group.items, scopes),
  }));
}

/**
 * The rail row the reader is ON: the item whose org-resolved href is the longest
 * prefix of `pathname`. Longest wins so a nested screen credits its OWN row —
 * `/fleet/plans/:id` lights Plans, not Fleet — and exactly one row is lit, which
 * a per-link prefix match cannot promise. `/` (and any `end` item) only matches
 * exactly. `undefined` when nothing matches.
 */
export function currentRailItem(
  pathname: string,
  items: readonly RailItem[],
  resolve: (href: string) => string,
): RailItem | undefined {
  const trim = (p: string): string => p.replace(/\/+$/, '') || '/';
  const here = trim(pathname).toLowerCase();
  let best: RailItem | undefined;
  let bestLength = -1;
  for (const item of items) {
    const href = trim(resolve(item.href)).toLowerCase();
    const exact = item.end === true || item.href === '/';
    const hit = exact ? here === href : here === href || here.startsWith(`${href}/`);
    if (hit && href.length > bestLength) {
      best = item;
      bestLength = href.length;
    }
  }
  return best;
}
